import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { Label } from '@/design_system/Label/Label';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import { useShowToast } from '@/design_system/Toast';
import IconError from '@/icons/Error.svg';
import { useExitRepair, useRepair } from '@/models/article';
import { InvoicePaymentStrategy } from '@/models/invoice';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

import { ArticleActionProps } from './ArticleActions';
import { ReportRepairIssueDialog } from './ReportRepairIssueDialog';

export const RepairArticleAction = ({ article, onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();
  const showToast = useShowToast();

  const hasNonDigitalizedWorkshopAndNoPaymentStrategy =
    !!article.workshop?.workshopConfig?.nonDigitalized && !article.workshopPaymentStrategy;

  const shouldOpenPaymentStrategyModal =
    hasNonDigitalizedWorkshopAndNoPaymentStrategy &&
    article.workshopOrganization?.workshopConfig?.payoutStrategy === 'handled-by-organization';

  const {
    mutateAsync: repair,
    isPending: isPendingRepair,
    isSuccess: isSuccessRepair,
  } = useRepair({
    articleId: article.id,
  });

  const {
    mutateAsync: exitRepair,
    isPending: isPendingExitRepair,
    isSuccess: isSuccessExitRepair,
  } = useExitRepair({
    articleId: article.id,
  });

  const [isPaymentStrategyModalOpen, setIsPaymentStrategyModalOpen] = useState(false);
  const [isReportRepairIssueDialogOpen, setIsReportRepairIssueDialogOpen] = useState(false);

  const confirmRepair = async ({
    paymentStrategy,
  }: {
    paymentStrategy?: InvoicePaymentStrategy;
  }) => {
    try {
      await repair({
        paymentStrategy,
      });
      onActionDone();
    } catch {
      showToast({
        type: 'error',
        text: t({
          id: 'article.actions.repair_article.error',
          message:
            'An error occurred while marking the article as repaired. Please try again later.',
        }),
      });
    } finally {
      setIsPaymentStrategyModalOpen(false);
    }
  };

  const confirmRepairExit = async (data: Endpoints['POST /articles/:id/exit-repair']['body']) => {
    try {
      await exitRepair(data);
      onActionDone();
    } catch {
      showToast({
        type: 'error',
        text: t({
          id: 'article.actions.repair_article.exit.error',
          message: 'An error occurred while reporting a repair issue. Please try again later.',
        }),
      });
    } finally {
      setIsReportRepairIssueDialogOpen(false);
    }
  };

  const isRepairLoading = isPendingRepair || isSuccessRepair;
  const isExitRepairLoading = isPendingExitRepair || isSuccessExitRepair;

  return (
    <>
      <Footer>
        <FooterButtons>
          <Button
            variant="secondary-danger"
            size="medium"
            onPress={() => setIsReportRepairIssueDialogOpen(true)}
            disabled={isRepairLoading}
          >
            <IconError />
            <Trans id="article.actions.repair_article.report">Report an issue</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={() => {
              if (shouldOpenPaymentStrategyModal) {
                setIsPaymentStrategyModalOpen(true);
              } else {
                confirmRepair({});
              }
            }}
            isLoading={isRepairLoading && !isPaymentStrategyModalOpen}
          >
            <Trans id="article.actions.repair_article.validate">Mark as repaired</Trans>
          </Button>
        </FooterButtons>
      </Footer>
      <WorkshopPaymentStrategy
        isOpen={isPaymentStrategyModalOpen}
        onClose={() => setIsPaymentStrategyModalOpen(false)}
        onConfirm={confirmRepair}
        isLoading={isPendingRepair}
      />
      <ReportRepairIssueDialog
        isOpen={isReportRepairIssueDialogOpen}
        onClose={() => setIsReportRepairIssueDialogOpen(false)}
        onConfirm={confirmRepairExit}
        isLoading={isExitRepairLoading}
      />
    </>
  );
};

const WorkshopPaymentStrategy = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { paymentStrategy: InvoicePaymentStrategy }) => void;
  isLoading: boolean;
}) => {
  const { article } = useArticleContext();

  const [paymentStrategy, setPaymentStrategy] = useState(article.workshopPaymentStrategy);
  const [showError, setShowError] = useState(false);

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <Trans id="article.actions.repair_article.workshop_payment_strategy.title">
          Payment to workshop information
        </Trans>
      }
      onOpenChange={onClose}
    >
      <main>
        <WorkshopPaymentStrategyInput
          paymentStrategy={paymentStrategy}
          setPaymentStrategy={setPaymentStrategy}
        />
      </main>
      <Footer>
        {showError && (
          <FooterError>
            <Trans id="article.actions.repair_article.workshop_payment_strategy.error">
              Please select the entity responsible for making the payment
            </Trans>
          </FooterError>
        )}
        <FooterButtons>
          <Button
            variant="primary"
            size="medium"
            onPress={() => {
              if (!paymentStrategy) {
                setShowError(true);

                return;
              }

              onConfirm({ paymentStrategy });
            }}
            isLoading={isLoading}
          >
            <span style={{ textWrap: 'nowrap' }}>
              <Trans id="article.actions.repair_article.workshop_payment_strategy.confirm">
                Confirm and validate repair
              </Trans>
            </span>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};

export const WorkshopPaymentStrategyInput = ({
  paymentStrategy,
  setPaymentStrategy,
}: {
  paymentStrategy: InvoicePaymentStrategy | null;
  setPaymentStrategy: (paymentStrategy: InvoicePaymentStrategy) => void;
}) => {
  const { t } = useLingui();

  return (
    <Label
      label={t({
        id: 'article.actions.repair_article.workshop_payment_strategy.payment_strategy.label',
        message: 'Who is responsible for making the payment?',
      })}
    >
      <RadioBoxGroup name="payment-strategy" value={paymentStrategy} onChange={setPaymentStrategy}>
        <RadioBox
          value="store-cash-out"
          ariaLabel={t({
            id: 'article.actions.repair_article.workshop_payment_strategy.payment_strategy.store_cash_out.text',
            message: 'The store',
          })}
          title={
            <Trans id="article.actions.repair_article.workshop_payment_strategy.payment_strategy.store_cash_out.text">
              The store
            </Trans>
          }
        >
          <Trans id="article.actions.repair_article.workshop_payment_strategy.payment_strategy.store_cash_out.sub_text">
            Via a cash register operation
          </Trans>
        </RadioBox>
        <RadioBox
          value="handled-by-accounting-team"
          ariaLabel={t({
            id: 'article.actions.repair_article.workshop_payment_strategy.payment_strategy.handled_by_accounting_team.text',
            message: 'The accounting department',
          })}
          title={
            <Trans id="article.actions.repair_article.workshop_payment_strategy.payment_strategy.handled_by_accounting_team.text">
              The accounting department
            </Trans>
          }
        >
          <Trans id="article.actions.repair_article.workshop_payment_strategy.payment_strategy.handled_by_accounting_team.sub_text">
            Via bank transfer at the end of the month
          </Trans>
        </RadioBox>
      </RadioBoxGroup>
    </Label>
  );
};
